import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { usePopperTooltip } from 'react-popper-tooltip'
import CrossIcon from 'components/Common/Icons/Cross'
import {
  Backdrop,
  Overlay,
  Highlight,
  Header,
  Title,
  Content,
  TooltipContent,
  CrossIconWrapper,
  Right,
  Left,
  Footer,
  WhiteButton,
  RedButton,
} from './_styles'
import { useElementVisible } from '../_methods'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getCurrentStepIndex, getSteps, setCurrentStep } from 'store/tutorial'
import { useFindElementWithRetry } from './_methods'
import { useCloseModalByEsc } from 'utils/hooks/useCloseModalByEsc'
import 'react-popper-tooltip/dist/styles.css'

const isObject = (value?: any): value is object => {
  return value !== null && typeof value === 'object'
}

interface ITutorialDialog {
  initialStep?: number
  onClose: (step: number) => void
  onFinish: () => void
}

const TutorialDialog = (props: ITutorialDialog) => {
  const { onClose, onFinish } = props
  const dispatch = useAppDispatch()
  const steps = useAppSelector(getSteps)
  const currentStep = useAppSelector(getCurrentStepIndex) || 0
  const [dimensions, setDimensions] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  })
  const { t } = useTranslation()
  const selector = steps[currentStep].elementSelector
  const element = useFindElementWithRetry(selector)
  const triggerElement = useFindElementWithRetry(
    steps[currentStep].triggerElementSelector || selector
  )
  const tooltipStyles = steps[currentStep].styles?.tooltip ?? {}

  const highlightGap = isObject(steps[currentStep].highlightGap)
    ? steps[currentStep].highlightGap
    : {
        top: steps[currentStep].highlightGap ?? 16,
        left: steps[currentStep].highlightGap ?? 16,
        right: steps[currentStep].highlightGap ?? 16,
        bottom: steps[currentStep].highlightGap ?? 16,
      }
  const placement = steps[currentStep].placement ?? 'bottom'
  const offset = steps[currentStep].offset || 0

  const isElementVisible = useElementVisible(element, {
    threshold: 0.1,
  })

  const modifiers = useMemo(
    () => [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [0, offset + 10],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          tether: false,
          padding: 8,
          boundary: document.body,
        },
      },
      {
        name: 'flip',
        options: {
          padding: 8,
          fallbackPlacements: ['top', 'bottom', 'left', 'right'],
          boundary: document.body,
        },
      },
    ],
    [offset]
  )

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
    update,
  } = usePopperTooltip(
    {
      trigger: null,
      visible: Boolean(element && dimensions?.width),
      placement,
      interactive: true,
    },
    { modifiers }
  )

  const updateDimensions = (targetElement: Element) => {
    const rect = targetElement.getBoundingClientRect()
    setDimensions({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
    })
  }

  useEffect(() => {
    if (steps.length === 0) return

    if (element) {
      element.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      })
      setTriggerRef(triggerElement as HTMLElement)
      updateDimensions(element)
    } else {
      setDimensions({ top: 0, left: 0, width: 0, height: 0 })
    }
  }, [
    currentStep,
    steps,
    isElementVisible,
    element,
    triggerElement,
    setTriggerRef,
  ])

  useEffect(() => {
    if (!element) return

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        updateDimensions(element)
        update?.()
      }
    })
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [element, update])

  // Отслеживание изменений размеров окна браузера
  useEffect(() => {
    const handleResize = () => {
      if (element) {
        updateDimensions(element)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useCloseModalByEsc(() => onClose(currentStep))

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      dispatch(setCurrentStep(currentStep + 1))
    } else {
      onFinish()
    }
  }
  const tooltipProps = getTooltipProps({ className: 'tooltip-container' })

  return (
    <Overlay isMounted={Boolean(element && dimensions?.width)}>
      {element && dimensions?.width > 0 && (
        <>
          <Backdrop />
          {dimensions.width && dimensions.height && (
            <Highlight dimensions={dimensions} gap={highlightGap} />
          )}
        </>
      )}
      {visible &&
        createPortal(
          <TooltipContent
            ref={setTooltipRef}
            {...tooltipProps}
            style={{ ...tooltipStyles, ...tooltipProps.style }}
          >
            <div {...getArrowProps({ className: 'tooltip-arrow' })} />
            <Header>
              <Title>{steps[currentStep].title}</Title>
              <CrossIconWrapper onClick={() => onClose(currentStep)}>
                <CrossIcon color={'#A0A1AD'}></CrossIcon>
              </CrossIconWrapper>
            </Header>
            <Content
              dangerouslySetInnerHTML={{ __html: steps[currentStep].content }}
            />
            <Footer>
              <Left>
                {currentStep + 1} of {steps.length}
              </Left>
              <Right>
                {currentStep !== steps.length - 1 && (
                  <WhiteButton onClick={() => onClose(currentStep)}>
                    {t('common.skip')}
                  </WhiteButton>
                )}
                <RedButton onClick={handleNext}>
                  {currentStep === steps.length - 1
                    ? t('retail.geoIndications.tutorial.finish')
                    : t('common.next')}
                </RedButton>
              </Right>
            </Footer>
          </TooltipContent>,
          document.getElementById('tooltips') || document.body
        )}
    </Overlay>
  )
}

export default TutorialDialog
